/**
 * shared/narration/lexicon.ts — the user-supplied half of §4.7, and the only lever a reader has.
 *
 * CHATTERBOX HAS NO SSML, NO PHONEMES AND NO PROSODY MARKUP. Verified on the live API twice
 * (2026-08-06, 2026-08-07). So "fix the pronunciation of *Sai*" cannot mean "attach a phoneme string
 * to that word" — there is no channel to attach it to. It means **spell it differently for the
 * synthesiser only**: the reader still renders `Sai`, and `sentences.ttsText` — the string that goes
 * to the GPU and the string hashed into `clipKey` — carries the respelling instead.
 *
 * That is not a workaround dressed up as a feature, and the UI says so in those words
 * (`client/src/reader/PronunciationSheet.tsx`). A user who does not know that the fix is
 * spelling-shaped will type `/saɪ/` into the box and hear it read out as slashes.
 *
 * ── This file is a VOCABULARY, not a second normaliser ──────────────────────
 *
 * There is exactly one normalisation path and it is `speakable()` in `./speakable.ts`, whose FIRST
 * stage is `applyOverrides` over `overridePattern()`. Everything here either builds the
 * `Record<printed, spoken>` that stage takes, or refuses input that would poison it. **Nothing here
 * rewrites text.** A second place that decides what a sentence says is the `fragments.ts` divergence
 * AGENTS.md opens with, one layer down: the planner would compute a `clipKey` from one answer and the
 * worker would render the other, and the page would re-narrate on every visit while every screen
 * reported success.
 *
 * ── Why validation lives here and is not advisory ───────────────────────────
 *
 * `overridePattern()` **swallows a regex that fails to compile and returns `null`** — deliberately: a
 * reader's pronunciation map must not be able to break their own book. But `null` means *the whole
 * map stops applying*, silently, for every sentence of every book. So the one bad key does not break
 * one word, it disables the lexicon. Refusing bad input at the door, with a sentence, is what keeps
 * that safety valve from becoming a silent loss — and it is why `validateEntry` runs on the client
 * (to render the message) and again on the server (to enforce it).
 *
 * `shared/` is bundled by the client: no I/O, no `node:` imports, every function pure.
 */

import { normalizeBlockText } from "../text/normalize.ts";
import { overridePattern } from "./speakable.ts";

// ─── the row, as both sides see it ───────────────────────────────────────────

export type PronunciationScope = "global" | "book";

/**
 * One `pronunciations` row, reduced to the fields that decide what is spoken.
 *
 * Structural rather than the drizzle row type, for the same reason `AccessBook` is structural in
 * `shared/access.ts`: a drizzle row, an API projection and a form under the user's cursor all satisfy
 * it without a mapping step, and a mapping step is where a `scope` gets dropped.
 */
export interface LexiconEntry {
  scope: PronunciationScope;
  /** Required when `scope === "book"`, and meaningless otherwise. */
  bookId?: string | null;
  /** The printed form, exactly as it appears in `blocks.text`. */
  pattern: string;
  /** How to spell it for the synthesiser. `""` means printed-but-not-spoken (see `speakable`). */
  replacement: string;
}

/**
 * The bounds, with the failure each one prevents attached.
 *
 * `patternMin = 2` is the sharpest of them. `overridePattern` guards a key that starts and ends with
 * a letter or a digit on both sides, so a one-character key like `I` matches every standing-alone
 * `I` in the library and there is no undo cheaper than re-narrating everything it touched. Two
 * characters is not a magic number; it is "long enough that a human meant it".
 *
 * `entriesPerScope` bounds the alternation `overridePattern` builds. It is ONE regex over every key,
 * run against every sentence of every renormalise, and an unbounded lexicon turns a pronunciation fix
 * into a table scan with a thousand-branch alternation on top.
 */
export const LEXICON_LIMITS = {
  patternMin: 2,
  patternMax: 80,
  replacementMax: 200,
  /** Per book, and separately for the global scope. */
  entriesPerScope: 400,
} as const;

// ─── canonical form ──────────────────────────────────────────────────────────

/**
 * The form a pattern must be stored in to have any chance of matching.
 *
 * `blocks.text` is NFC, invisible-stripped and whitespace-collapsed (`normalizeBlockText`, run at
 * ingest), and `sentences.text` is a slice of it. A pattern pasted out of a PDF with a non-breaking
 * space, a soft hyphen or a trailing newline in it is a key that can never match anything, and the
 * user sees "0 sentences" with nothing to explain it. Canonicalising with the SAME function ingest
 * used is the only version of this that cannot drift.
 *
 * Case is deliberately NOT folded. `overridePattern` is case-sensitive, `Polish` and `polish` are two
 * different words, and a lexicon that quietly matched both would be unable to express the difference.
 * The affected-sentence count is what tells a user their capitalisation is wrong — which is one of
 * the reasons that count is shown before anything is written.
 */
export const canonicalPattern = (raw: string): string => normalizeBlockText(raw);

/**
 * The replacement gets the same treatment minus the emptiness rule: `""` is a legal, documented value
 * meaning *printed but not spoken*, and `speakable()` drops the utterance for it.
 */
export const canonicalReplacement = (raw: string): string => normalizeBlockText(raw);

// ─── validation ──────────────────────────────────────────────────────────────

export interface LexiconProblem {
  field: "pattern" | "replacement";
  /** A machine code, like the ingest refusals in `client/src/lib/api.ts`. */
  code:
    | "pattern-empty"
    | "pattern-too-short"
    | "pattern-too-long"
    | "pattern-not-a-word"
    | "pattern-uncompilable"
    | "replacement-too-long"
    | "replacement-unchanged";
  /** What it refused and what would fix it — AGENTS.md's refusal rule. */
  message: string;
}

/** A string with no letter and no digit anywhere in it. */
const HAS_WORD_CHARACTER = /[\p{L}\p{N}]/u;

/**
 * `null` when the entry is storable. One problem at a time, in the order a person fixes them.
 *
 * Both sides call it: the sheet to render the message under the field, the route to enforce it.
 * Two implementations of one rule diverge on the case nobody screenshotted (LAW 13's reasoning,
 * applied to a validator rather than to a permission).
 */
export function validateEntry(entry: { pattern: string; replacement: string }): LexiconProblem | null {
  const pattern = canonicalPattern(entry.pattern);
  const replacement = canonicalReplacement(entry.replacement);

  if (pattern === "") {
    return {
      field: "pattern",
      code: "pattern-empty",
      message: "Type the word as it is printed in the book — that is what the fix keys off.",
    };
  }
  if ([...pattern].length < LEXICON_LIMITS.patternMin) {
    return {
      field: "pattern",
      code: "pattern-too-short",
      message:
        "A one-character rule rewrites that character everywhere it stands alone in the book, and there is no cheap undo. " +
        "Use at least two characters — the whole name, or the phrase around it.",
    };
  }
  if (pattern.length > LEXICON_LIMITS.patternMax) {
    return {
      field: "pattern",
      code: "pattern-too-long",
      message: `That is longer than ${LEXICON_LIMITS.patternMax} characters. A whole sentence is not a pronunciation — respell just the word, or use "Regenerate with different text" on that one sentence.`,
    };
  }
  if (!HAS_WORD_CHARACTER.test(pattern)) {
    return {
      field: "pattern",
      code: "pattern-not-a-word",
      message:
        "That has no letters or digits in it, so it would match punctuation anywhere in the book. Include the word itself.",
    };
  }
  // `overridePattern` escapes its keys, so this is close to unreachable — which is exactly why it is
  // checked rather than assumed. If it ever DOES return null, the whole lexicon silently stops
  // applying, and the single worst place to discover that is a book that has already been narrated.
  if (overridePattern({ [pattern]: replacement }) === null) {
    return {
      field: "pattern",
      code: "pattern-uncompilable",
      message: "That text can't be turned into a matching rule. Try the plain word, without brackets or slashes.",
    };
  }
  if (replacement.length > LEXICON_LIMITS.replacementMax) {
    return {
      field: "replacement",
      code: "replacement-too-long",
      message: `Keep the spoken form under ${LEXICON_LIMITS.replacementMax} characters. It is a respelling, not a footnote.`,
    };
  }
  if (replacement === pattern) {
    return {
      field: "replacement",
      code: "replacement-unchanged",
      message: "That is the same spelling, so nothing would change. Spell it the way it sounds — Siobhan → Shiv-awn.",
    };
  }
  return null;
}

// ─── merging ─────────────────────────────────────────────────────────────────

/**
 * The `Record<printed, spoken>` that `speakable({ overrides })` takes, for ONE book.
 *
 * Two precedence rules, and they answer two different questions:
 *
 *  · **Scope decides a tie on the same key.** A per-book row beats a global row spelled identically —
 *    that is what "per-book overrides the lexicon" means, and it is the escape hatch for the book
 *    where `Mark` really is a man and not the gospel.
 *  · **Length decides overlapping keys**, and it is not decided here: `overridePattern` sorts longest
 *    first for exactly this. books.karti.ai earned it — with keys for both `41` and `41.22`, the
 *    shorter one eats the prefix and leaves `forty-one.22`.
 *
 * Rows carrying a `bookId` other than this book's are dropped rather than trusted, because a global
 * merge is the one place a book-scoped fix could leak into a stranger's book.
 */
export function mergeLexicon(
  entries: readonly LexiconEntry[],
  bookId?: string | null,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of entries) {
    if (e.scope !== "global") continue;
    const key = canonicalPattern(e.pattern);
    if (key !== "") out[key] = canonicalReplacement(e.replacement);
  }
  if (bookId != null && bookId !== "") {
    for (const e of entries) {
      if (e.scope !== "book" || e.bookId !== bookId) continue;
      const key = canonicalPattern(e.pattern);
      if (key !== "") out[key] = canonicalReplacement(e.replacement);
    }
  }
  return out;
}

// ─── which sentences can this touch? ─────────────────────────────────────────

/**
 * The cheap, over-inclusive test: does this text contain the pattern as a literal substring?
 *
 * ⚠️ **This is the predicate the SQL prefilter implements, and the two must agree or the invalidation
 * is incomplete.** `server/src/books/renormalize.ts` narrows a scan with `instr(text, ?) > 0`, which
 * is SQLite's spelling of exactly this and is case-sensitive in the same way. Its correctness rests on
 * one property of `overridePattern`: every alternative it builds is the key ESCAPED as a literal,
 * wrapped in lookaround guards that consume nothing. So a sentence the pattern can rewrite must
 * contain the pattern verbatim — the prefilter is a superset of the matches, never a subset.
 *
 * If anyone ever gives a lexicon key regex powers, this function and that `instr` become wrong
 * together and pronunciations start applying to sentences the invalidation pass never looked at:
 * `ttsText` changes for a sentence whose `sentence_clips` row still points at the old clip, and the
 * page plays the old audio forever. Do not give lexicon keys regex powers.
 */
export const containsPattern = (text: string, pattern: string): boolean => text.includes(pattern);

/**
 * The exact test: would this one entry rewrite anything in this text?
 *
 * Built from `overridePattern` — the same function `speakable` stage 1 uses — rather than from a
 * hand-rolled boundary check, so "the sheet says it matches" and "the normaliser rewrites it" can
 * never be two different answers. Used for the *did you mean this?* warning when a pattern matches
 * nothing at all, which is almost always a capitalisation the user got wrong.
 */
export function countMatches(text: string, pattern: string): number {
  const key = canonicalPattern(pattern);
  if (key === "") return 0;
  const re = overridePattern({ [key]: "" });
  if (re === null) return 0;
  let n = 0;
  // `overridePattern` returns a fresh `g`-flagged regex on every call, so `lastIndex` starts at 0 and
  // this loop cannot be poisoned by a previous caller's cursor.
  while (re.exec(text) !== null) n++;
  return n;
}

// ─── what a change costs, in one wording ─────────────────────────────────────

/**
 * §4.3's *"Normalizer v7: 41 of 12,904 sentences changed · 3 min of GPU · re-narrate?"*, and the P3
 * gate's *"promote it to the lexicon and see the affected-books count"* — one shape, computed by
 * `server/src/books/renormalize.ts` and rendered by the sheet.
 */
export interface LexiconImpact {
  /** Distinct books whose current edition has at least one sentence rewritten. */
  books: number;
  editions: number;
  /** Sentences whose `ttsText` actually changes. Not sentences the pattern appears in. */
  sentences: number;
  /**
   * Sentences that change on paper and cost NOTHING, because a per-sentence respelling
   * (`sentence_overrides.ttsText`) already replaces their `ttsText` wholesale and the planner reads
   * that instead. Counted separately so the GPU number is not inflated by work nobody will do.
   */
  shadowed: number;
  /** Characters that must be re-synthesised — the input to the GPU estimate, and only that. */
  chars: number;
  /** Pages that are narrated TODAY and contain a changed sentence: the audio that stops being current. */
  pagesNarrated: number;
  /** From `shared/narration/rates.ts`. Never a literal — §2.4. */
  estGpuSec: number;
}

export const EMPTY_IMPACT: LexiconImpact = {
  books: 0,
  editions: 0,
  sentences: 0,
  shadowed: 0,
  chars: 0,
  pagesNarrated: 0,
  estGpuSec: 0,
};

/**
 * *"affects 7 books · 214 sentences · 4 min of GPU"* — the line the sheet shows BEFORE the button
 * commits anything.
 *
 * It lives here, next to the shape it describes, so the server's audit detail and the client's label
 * are one sentence rather than two that drift. §2.4's rule is about rate constants; the same argument
 * applies to the copy that reports them.
 */
export function describeImpact(i: LexiconImpact): string {
  if (i.sentences === 0) return "nothing changes — no sentence uses that spelling";
  const parts: string[] = [];
  if (i.books > 1) parts.push(`${i.books} books`);
  parts.push(`${i.sentences.toLocaleString("en-GB")} ${i.sentences === 1 ? "sentence" : "sentences"}`);
  if (i.pagesNarrated > 0) {
    parts.push(`${i.pagesNarrated} narrated ${i.pagesNarrated === 1 ? "page" : "pages"}`);
  }
  // ⚠️ Zero GPU is a COMMON answer and it has to read like one, not like a missing number.
  //
  // Two ordinary cases produce it: a book nobody has narrated yet (nothing exists to redo), and an
  // undo — every sentence going back lands on a `clipKey` whose clip was never evicted, so the audio
  // is already there. `${gpuLabel(0)} of GPU` composes to "no of GPU", which reads as a bug and makes
  // the whole line untrustworthy on exactly the changes that are safest to make.
  parts.push(i.estGpuSec > 0 ? `${gpuLabel(i.estGpuSec)} of GPU` : "no GPU to re-do");
  return `affects ${parts.join(" · ")}`;
}

/**
 * Seconds under a minute and a half, minutes above it.
 *
 * "90 s" and "1.5 min" are the same number and only one of them is how a person decides whether to
 * press the button. Nothing is ever rendered as "0 min".
 */
export function gpuLabel(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return "no";
  if (sec < 90) return `about ${Math.max(1, Math.round(sec))} s`;
  return `about ${Math.round(sec / 60)} min`;
}
